// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { fuelDonut, mixBarHtml, fuelLegendHtml } from '../components/charts';
import { FUEL_COLOUR, FUEL_LABEL, FUEL_ORDER, type Fuel } from '../fuels';
import { glossaryTerm, infoIcon } from '../glossary';
import { formatCompact, formatNumber, formatPercent } from '../utils/format';

export function renderOverview(root: HTMLElement, ctx: ViewContext): void {
  const { meta, national } = ctx.data;
  const n = meta.national;

  const stat = (label: string, value: string, cls = '', note = '', term = '') => `
    <div class="stat">
      <div class="stat-label">${label}${term ? ` ${infoIcon(term)}` : ''}</div>
      <div class="stat-value ${cls}">${value}</div>
      ${note ? `<div class="stat-note">${note}</div>` : ''}
    </div>`;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The fleet still runs on petrol and diesel</h1>
      <p class="view-sub">
        Every one of the ${formatNumber(n.totalAll)} road vehicles ${glossaryTerm('registered', 'registered')} in
        Australia on ${meta.snapshotDate}. Electric cars are in every headline — but they are still a rounding error on
        the road, because the fleet is a ${glossaryTerm('stock', 'slow-moving stock')}, not this year's sales.
      </p>
    </div>

    <div class="hero">
      <div class="panel">
        <p class="hero-lead">
          Among Australia's ${formatNumber(n.passengerTotal)} ${glossaryTerm('passenger', 'passenger cars')}, just
          <strong>${formatPercent(n.bevShare, 2)}</strong> are fully ${glossaryTerm('bev', 'electric')} — while
          <strong>${formatPercent(n.dieselShare)}</strong> run on diesel. So there is:
        </p>
        <div class="big-ratio">${n.dieselToBevRatio != null ? `${n.dieselToBevRatio.toFixed(1)}×` : '—'}</div>
        <p class="hero-lead">more diesel than battery-electric on the road. The quiet story of the last decade isn't
          petrol→electric — it's petrol→diesel, as Australia bought utes and big SUVs.</p>
        <div class="callout">
          <strong>Sales ≠ the fleet.</strong> ${glossaryTerm('electrified', 'Electrified')} cars are
          ${formatPercent(n.evShare)} of the whole fleet, but ${n.newEvShare != null ? formatPercent(n.newEvShare) : '—'}
          of cars built in ${meta.newestCompleteYom}. See the
          <button class="chip" data-goto="newcar" style="font-family:var(--font-sans)">New-Car Effect →</button>
        </div>
      </div>
      <div class="panel">
        <div class="panel-title">Passenger fleet by fuel</div>
        <div class="panel-sub">Share of ${formatNumber(n.passengerTotal)} cars, ${meta.snapshotDate}.</div>
        <div style="display:flex;gap:var(--space-lg);align-items:center;flex-wrap:wrap;justify-content:center">
          <div data-role="donut" style="flex-shrink:0"></div>
          <div class="legend" style="flex-direction:column;gap:6px;margin:0" data-role="legend"></div>
        </div>
      </div>
    </div>

    <div class="stat-grid">
      ${stat('All road vehicles', formatCompact(n.totalAll), 'slate', 'cars, utes, trucks, bikes')}
      ${stat('Passenger cars', formatCompact(n.passengerTotal), '', '', 'passenger')}
      ${stat('Fully electric', formatPercent(n.bevShare, 2), 'green', `${formatNumber(n.bev)} cars`, 'bev')}
      ${stat('Electrified', formatPercent(n.evShare), '', 'incl. hybrids', 'electrified')}
      ${stat('Diesel', formatPercent(n.dieselShare), 'diesel', `${formatNumber(n.diesel)} cars`)}
      ${stat('Typical age', n.avgAgeYears != null ? `${n.avgAgeYears} yrs` : '—', 'slate', n.medianYear != null ? `median build ${n.medianYear}` : '', 'yom')}
    </div>

    <div class="panel" style="margin-bottom:var(--space-xl)">
      <div class="panel-title">The whole passenger fleet, one bar</div>
      <div class="panel-sub">Every car on the road on ${meta.snapshotDate}, by fuel type. Hover a segment for the count.</div>
      ${mixBarHtml(national.passengerMix, FUEL_ORDER, { tall: true })}
      ${fuelLegendHtml(FUEL_ORDER)}
    </div>

    <div class="panel">
      <div class="panel-title">Not all vehicles are alike</div>
      <div class="panel-sub">Fuel mix by vehicle type — passenger cars are petrol, but the working fleet is diesel. Open <button class="chip" data-goto="types" style="font-family:var(--font-sans)">Vehicle Types →</button></div>
      <div data-role="types"></div>
    </div>
  `;

  // Donut + legend.
  (root.querySelector('[data-role="donut"]') as HTMLElement).appendChild(fuelDonut(national.passengerMix, FUEL_ORDER, { size: 190 }));
  (root.querySelector('[data-role="legend"]') as HTMLElement).innerHTML = FUEL_ORDER.map((f: Fuel) => {
    const pct = national.passengerMix.total > 0 ? (national.passengerMix[f] / national.passengerMix.total) * 100 : 0;
    return `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${FUEL_COLOUR[f]}"></span>${FUEL_LABEL[f]} <strong style="font-family:var(--font-mono);margin-left:4px">${formatPercent(pct)}</strong></span>`;
  }).join('');

  // Vehicle-type preview mix bars.
  const typesSlot = root.querySelector('[data-role="types"]') as HTMLElement;
  typesSlot.innerHTML = national.byType
    .filter((t) => t.total > 50000)
    .slice(0, 6)
    .map(
      (t) => `
      <div style="display:flex;align-items:center;gap:var(--space-md);margin-bottom:8px">
        <div style="width:180px;flex-shrink:0;font-size:var(--font-size-sm);color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0">${t.type}</div>
        <div style="flex:1;min-width:0">${mixBarHtml(t, FUEL_ORDER)}</div>
        <div style="width:60px;flex-shrink:0;text-align:right;font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-tertiary)">${formatCompact(t.total)}</div>
      </div>`,
    )
    .join('');

  root.querySelectorAll('[data-goto]').forEach((b) =>
    b.addEventListener('click', () => ctx.goTo(b.getAttribute('data-goto') as any)),
  );
}
